import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

interface JobChangeDetail {
  field: string;
  oldValue: unknown;
  newValue: unknown;
  fieldLabel: string;
}

const TRACKED_FIELDS = [
  { key: 'title', label: 'Title' },
  { key: 'description', label: 'Description' },
  { key: 'category', label: 'Category' },
  { key: 'due_date', label: 'Due Date' },
  { key: 'art_files_ready_date', label: 'Art Files Ready Date' },
  { key: 'project_name', label: 'Project Name' },
  { key: 'internal_reference', label: 'Internal Reference' },
  { key: 'client_notes', label: 'Client Notes' },
  { key: 'estimated_start_date', label: 'Estimated Start Date' },
  { key: 'estimated_completion_date', label: 'Estimated Completion Date' },
];

/**
 * Normalizes a value for comparison, handling dates, nulls, and empty strings
 */
function normalizeValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  if (typeof value === 'string') {
    // Try to normalize ISO date strings to date-only for comparison
    const dateMatch = value.match(/^\d{4}-\d{2}-\d{2}/);
    if (dateMatch) {
      return dateMatch[0];
    }
    return value;
  }
  return JSON.stringify(value);
}

/**
 * Compares old and new job data and logs changes to the audit_logs table
 */
export async function logJobChanges(
  jobId: string,
  userId: string,
  oldJob: Record<string, unknown>,
  newJob: Record<string, unknown>
): Promise<void> {
  const changes: JobChangeDetail[] = [];

  for (const field of TRACKED_FIELDS) {
    const oldVal = normalizeValue(oldJob[field.key]);
    const newVal = normalizeValue(newJob[field.key]);

    if (oldVal !== newVal) {
      changes.push({
        field: field.key,
        oldValue: oldJob[field.key],
        newValue: newJob[field.key],
        fieldLabel: field.label,
      });
    }
  }

  // No changes detected, skip logging
  if (changes.length === 0) {
    return;
  }

  const detailsPayload: Json = {
    changes: changes.map(c => ({
      field: c.field,
      oldValue: c.oldValue as Json,
      newValue: c.newValue as Json,
      fieldLabel: c.fieldLabel,
    })),
  };

  const { error } = await supabase.from('audit_logs').insert([{
    user_id: userId,
    action: 'job_field_updated',
    resource_type: 'job',
    resource_id: jobId,
    details: detailsPayload,
  }]);

  if (error) {
    console.error('Failed to log job changes:', error);
  }
}
